





/**
 * This is an extension to Leaflet allowing to display the TomTom traffic incidents layer.
 *
 * It uses the tomtom.trafficIncidents service in order to retrieve incidents details.
 *
 * A look and feel, together with a clustering behaviour, can be modified by passing the pointToLayer function. The
 * default implementation looks as follows
 * ```javascript
 *
 * define(['leaflet', 't', 'unitFormatConverter/unitFormatConverter'], function (L, T, units) {
 *   'use strict';
 *
 *   var iconsMapping = {
 *           'Unknown': 'danger', 'Accident': 'accident', 'Fog': 'danger', 'Dangerous Conditions':
 *           'danger', 'Rain': 'danger', 'Ice': 'ice', 'Jam': 'incident', 'Lane Closed':
 *           'laneclosed', 'Road Closed': 'roadclosed', 'Road Works': 'roadwork', 'Wind': 'danger',
 *           'Flooding': 'danger', 'Detour': 'danger', 'Cluster': ''
 *       },
 *       iconTemplate = '<div class="traffic-icon"><div class="icon-circle-{{=incidentSeverity}}">' +
 *           '<div class="icon-{{=icon}}-white"></div></div></div>',
 *       shieldTemplate = '{{roadNumber}}<div class="road-shield">{{=roadNumber}}</div>' +
 *       '{{/roadNumber}}',
 *       descriptionTemplate = '<div class="traffic-description"><div class="incident-category">' +
 *       '{{=incidentCategory}}</div><div class="incident-delay-length"{{!delaySeconds}}' +
 *       '{{!lengthMeters}} style="display:none;"{{/!lengthMeters}}{{/!delaySeconds}}>' +
 *       '{{delaySeconds}}<div class="incident-delay">{{=time}}</div>{{/delaySeconds}}' +
 *       '{{lengthMeters}}<div class="incident-length">{{=distance}}</div>{{/lengthMeters}}</div>' +
 *       '</div>',
 *       messageTemplate = new T('<div class="traffic-details">' + iconTemplate + shieldTemplate +
 *       descriptionTemplate + '</div>');
 *
 *   function trafficIncidentMessage(info, imperialDistance) {
 *       info.distance = units.formatDistance(info.lengthMeters, imperialDistance);
 *       info.time = units.formatTime(info.delaySeconds);
 *       info.incidentSeverity = info.incidentSeverity || 'unknown';
 *       info.icon = iconsMapping[info.incidentCategory];
 *       return messageTemplate.render(info);
 *   }
 *
 *   function clusterClickHandler(bounds, latlng) {
 *       if (bounds) {
 *           return function () {
 *               this._map.fitBounds(bounds);
 *           };
 *       } else {
 *           return function () {
 *               this._map.setZoomAround(latlng, this._map.getZoom() + 2);
 *           };
 *       }
 *   }
 *
 *   return function (feature, latlng, imperialDistance) {
 *       var iconOptions = {
 *           background: {
 *               iconSize: L.Browser.retina ? [34, 34] : [20, 20],
 *               iconAnchor: L.Browser.retina ? [17, 17] : [10, 10],
 *               icon: ''
 *           },
 *           icon: {
 *               iconSize: L.Browser.retina ? [34, 34] : [20, 20],
 *               iconAnchor: [0, 0],
 *               icon: ''
 *           },
 *           content: {
 *               iconSize: L.Browser.retina ? [34, 34] : [20, 20],
 *               iconAnchor: [0, 0],
 *               content: '',
 *               'style': {
 *                   'color': '#fff',
 *                   'font-weight': 'bold',
 *                   'font-size': L.Browser.retina ? '18px' : '11px'
 *               }
 *           },
 *           popupAnchor: L.Browser.retina ? [0, -8] : [0, -5]
 *       }, marker;
 *
 *       if (!feature || !feature.properties) {
 *           return null;
 *       }
 *
 *       iconOptions.background.icon = 'icon-circle-' +
 *           (feature.properties.incidentSeverity || 'unknown');
 *
 *       if (feature.properties.clusterSize) {
 *           iconOptions.icon = null;
 *           iconOptions.content.content = feature.properties.clusterSize < 100
 *              ? feature.properties.clusterSize:'99+';
 *           marker = L.marker(latlng, {icon: L.svgIcon(iconOptions)}); marker.on('click',
 *               clusterClickHandler(feature.properties.clusterBounds, latlng));
 *       } else {
 *           iconOptions.content = null;
 *           iconOptions.icon.icon = 'icon-' + iconsMapping[feature.properties.incidentCategory] +
 *              '-white';
 *           marker = L.marker(latlng, {icon: L.svgIcon(iconOptions)});
 *           marker.bindPopup(trafficIncidentMessage(feature.properties, imperialDistance),
 *              {className: 'incidents-popup'});
 *       }
 *       return marker;
 *   };
 * });
 * ```
 *
 * @class TomTomTrafficIncidentsLayer
 * @extends L.GeoJSON
 * @namespace L
 * @module Map
 * @constructor
 * @param {Object} [options] It accepts all the options that
 *     <a href="http://leafletjs.com/reference-1.3.0.html#geojson-options">Leaflet's GeoJSON</a>
 *     class allows.
 *     The additional options, which are TomTom specific, are described below.
 * @param {String} [options.key] API key that can be obtained from
 *     <a href="http://developer.tomtom.com">TomTom's developer portal</a>.
 *     This is a key for the traffic services.
 * @param {Function} [options.pointToLayer=default] This function is used to convert vector traffic
 *     incidents into a layer of markers on the map. It takes the GeoJSON feature as the first parameter and the latlng
 *     object as the second one. It returns a layer that will be added to the map. If this is omitted, the default
 *     implementation is used (displayed above).
 * @param {Boolean} [options.imperialDistance=null] Set this option if you want to use a specific unit measurement
 *     system for the new instance of the layer instead of use the default unit
 * @param {Boolean} [options.createPopupForCluster=false] Set this to true if you want popup for clusters to be
 *     generated
 * @param {Boolean} [options.diff=false] If false, force a 'full' update on traffic markers.
 * @param {Boolean} [options.noRefresh] Disables periodic updates of traffic layers.
 * @param {Number} [options.refresh=30000] The traffic incidents information changes and needs to be updated
 *     periodically. By default, it is refreshed every 30 seconds. This parameter's value represents the time
 *     in milliseconds between each update interval. The value must be 0 or greater.
 */
/* jshint nomen:false */

define([
    'leaflet',
    'security',
    'services/trafficIncidents',
    './defaultIncidentsLayer',
    '../../config/unit/unitTypes',
    './trafficRefresher',
    'services/utils'
], function(
    L,
    security,
    trafficIncidents,
    defaultPointToLayer,
    unitTypes,
    trafficRefresher,
    Utils
) {

    var trafficStyles = ['s1', 's2', 's3', 'night'];

    //mix and max zoom of the incidents layer have been defined as constants because they should not be overwritten;
    //also the service does not allow values greater than 18
    var INCIDENTS_LAYER_MIN_ZOOM = 7;
    var INCIDENTS_LAYER_MAX_ZOOM = 18;

    var EVENTS = {
        /**
         * Fired when new incidents data is received.
         *
         * @event TRAFFIC_INCIDENTS_RECEIVED
         * @param {Object} data GeoJson data
         * @param {L.Marker[]} layers Array of markers created to display the incidents
         */
        TRAFFIC_INCIDENTS_RECEIVED: 'tomtom.trafficincidentsreceived'
    };

    function getTrafficStyle(trafficLayer) {
        var style = 'night', i;
        if (Utils.isString(trafficLayer)) {
            for (i = 0; i < trafficStyles.length; i += 1) {
                if (trafficStyles[i] === trafficLayer.toLowerCase()) {
                    return trafficStyles[i];
                }
            }
        }
        return style;
    }

    function createPointToLayerHandler(layer) {
        return function(feature, latlng) {
            return defaultPointToLayer(feature, latlng, layer._shouldUseImperialDistance(), layer.options);
        };
    }

    /**
     * @ignore
     * In order to differentiate markers to remove when traffic data updates. This flag is added.
     * @param {Function} pointToLayer
     * @return {Function}
     */
    function wrapPointToLayer(pointToLayer) {
        return function(feature, latlng) {
            var marker = pointToLayer(feature, latlng);
            marker.__isTrafficMarker = true;
            return marker;
        };
    }

    L.TomTomTrafficIncidentsLayer = L.GeoJSON.extend({
        options: {
            imperialDistance: null,
            trafficModelId: -1,
            minZoom: INCIDENTS_LAYER_MIN_ZOOM
        },
        /**
         * Name of traffic incidents layer. It can be used to differentiate layers.
         * @property name
         * @default "TomTomTrafficIncidentsLayer"
         * @type String
         */
        name: 'trafficIncidents',
        Events: EVENTS,
        statics: {
            Events: EVENTS
        },
        /**
         * @ignore
         * First check the configuration for this instance and then the global configuration.
         * The instance configuration has priority.
         *
         * @return {Boolean}
         */
        _shouldUseImperialDistance: function() {
            if (typeof this.options.imperialDistance === 'boolean') {
                return this.options.imperialDistance;
            }
            return this.unitService.getCurrentUnit().getLabel() === unitTypes.imperial;
        },
        initialize: function(options) {
            L.Util.setOptions(this, options);
            this._layers = {};
            security.setKey('services/traffic', options.key);
            this.incidentsService = trafficIncidents(options.trafficService)
                .key(security.getKey('services/traffic'))
                .style(getTrafficStyle(options.style));
            this.options.pointToLayer = wrapPointToLayer(this.options.pointToLayer || createPointToLayerHandler(this));
        },
        onAdd: function(map) {
            this._map = map;
            this.unitService = map.getUnitService();
            this.localeService = map.getLocaleService();
            map.on({'viewreset': this.redraw, 'moveend': this.update}, this);
            this.unitService.on(L.UnitService.Events.UNIT_CHANGED, this.redraw, this);
            this.localeService.on(L.LocaleService.Events.LOCALE_CHANGED, this.redraw, this);
            this.trafficRefresherIntervalId =
                trafficRefresher.addRefresherWithModelId(undefined, this.setTrafficModelId, this);
        },
        onRemove: function(map) {
            this.unitService.off(L.UnitService.Events.UNIT_CHANGED, this.redraw, this);
            map.off({'viewreset': this.redraw, 'moveend': this.update}, this);
            trafficRefresher.remove(this.trafficRefresherIntervalId);
            this.clearLayers();
        },
        _getIncidentsService: function(trafficModelId) {
            var map = this._map, zoom, serviceCall;
            if (!map) {
                return null;
            }
            // although the vector map zoom in levels can go into great detail, the incidents layer
            // only supports up to zoom level 18
            zoom = map.getZoom() <= INCIDENTS_LAYER_MAX_ZOOM ? map.getZoom() : INCIDENTS_LAYER_MAX_ZOOM;

            if (zoom < INCIDENTS_LAYER_MIN_ZOOM) {
                this.clearLayers();
                return null;
            }
            serviceCall = this.incidentsService
                .boundingBox(map.getBounds())
                .zoomLevel(zoom)
                .language(this.localeService.getCurrentLocale().getValueFor('traffic'));
            if (typeof trafficModelId !== 'undefined') {
                serviceCall = serviceCall.trafficModelID(trafficModelId);
            }
            return serviceCall;
        },
        // In this method we have to do few things:
        // 1. find layers (markers) that should be removed, because they are not present in the new response
        // 2. find layers that can stay and update them (IF NECESSARY)
        _updateMarkers: function(geoJSON) {
            // In this object we are storing ids as keys (to speed up the searching process), that are pointing
            // to corresponding object in the geoJSON response from the API.
            var newFeaturesDictionary = geoJSON.features.reduce(function(result, feature) {
                var current = {};
                current[feature.properties.id] = feature;
                return Object.assign(result, current);
            }, Object.create(null));

            // eachLayer is iterating over old, already existing markers from the prev response
            this.eachLayer(function(layer) {
                // layer.feature.properites has the same structure as new markers in geoJSON, we are are using this
                // object as a reference to the original feature from the previous response.
                var newFeatures;
                var properties = layer.feature.properties;

                // if we can find current marker in the new response it stays
                if (!(properties.id in newFeaturesDictionary)) {
                    this.removeLayer(layer);
                    return;
                }

                // From time to time markers with the same id have some useless props (like `time: undefined`) and we
                // could ignore them, BUT what if something else will change? We're doing deep equal and update
                // icon and popup data.
                if (!Utils.isEqual(properties, newFeaturesDictionary[properties.id].properties) &&
                    typeof layer._updateIcon === 'function' &&
                    typeof layer._updatePopup === 'function'
                ) {
                    newFeatures = newFeaturesDictionary[properties.id];
                    layer._updateIcon(newFeatures);
                    layer._updatePopup(newFeatures);
                }

                // We already have a layer for this feature, let's keep it on the map
                delete newFeaturesDictionary[properties.id];
            }, this);

            // Here we use Object.assign because we don't want to assume that we know geoJSON structure, it's safer
            // to just update features property and keep the rest as it was. And we are doing that because outside
            // of this method we need to have original geoJSON with every feature we've got from the API.
            return Object.assign({}, geoJSON, {
                features: Object.keys(newFeaturesDictionary).map(function(key) {
                    return newFeaturesDictionary[key];
                })
            });
        },
        _handleResponse: function(geoJSON) {
            var data = geoJSON;
            if (this.options.diff) {
                data = this._updateMarkers(geoJSON);
            } else {
                this.clearLayers();
            }
            this
                .addData(data)
                .fire(L.TomTomTrafficIncidentsLayer.Events.TRAFFIC_INCIDENTS_RECEIVED, {
                    data: geoJSON,
                    layers: this.getLayers()
                });
        },
        _request: function(trafficModelId) {
            var preconfiguredIncidentsService = this._getIncidentsService(trafficModelId);
            if (!preconfiguredIncidentsService) {
                return null;
            }
            return preconfiguredIncidentsService.go()
                .then(this._handleResponse.bind(this));
        },
        redraw: function() {
            return this._request(this.options.trafficModelId);
        },
        update: function() {
            return this._request();
        },
        setTrafficModelId: function(trafficModelId) {
            if (this.options.trafficModelId !== trafficModelId) {
                this.options.trafficModelId = trafficModelId;
                this.redraw();
            }
        },
        updateOptions: function(options) {
            trafficRefresher.remove(this.trafficRefresherIntervalId);
            this.trafficRefresherIntervalId =
                trafficRefresher.addRefresherWithModelId(options, this.setTrafficModelId, this);
            L.setOptions(this, options);
        }
    });

    L.tomTomTrafficIncidentsLayer = function(options) {
        return new L.TomTomTrafficIncidentsLayer(options);
    };

    return L.tomTomTrafficIncidentsLayer;
});



// WEBPACK FOOTER //
// ./display/layers/trafficIncidentsLayer.js